import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, Search, Check, X } from 'lucide-react';
import { formatCurrency, isAdminUser, canModifyRecord, approvalBadge } from '../utils/helpers';

const empty = {
  name: '', party_type: 'both', contact_person: '', phone: '', email: '',
  gstin: '', address: '', city: '', state: '', pincode: '',
  credit_limit: 0, opening_balance: 0, opening_balance_date: '',
  balance_type: 'receivable', status: 1, notes: '', default_vehicle_id: ''
};

const typeLabel = (t) => ({ supplier: 'Supplier', customer: 'Customer', both: 'Both' }[t] || 'Both');
const typeBadge = (t) => ({ supplier: 'badge-amber', customer: 'badge-blue', both: 'badge-green' }[t] || 'badge-gray');

export default function Ledgers() {
  const [list, setList] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [q, setQ] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterApproval, setFilterApproval] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const admin = isAdminUser();

  const load = () => api.get('/ledgers').then(r => setList(r.data.data || []));
  useEffect(() => {
    load();
    api.get('/vehicles').then(r => {
      // Only show approved vehicles in the dropdown
      setVehicles((r.data.data || []).filter(v => v.approval_status === 'approved' && v.status !== 'inactive'));
    }).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.name) return toast.error('Name is required');
    try {
      if (editId) await api.put('/ledgers/' + editId, form);
      else await api.post('/ledgers', form);
      const msg = !editId && !admin ? 'Submitted for admin approval' : 'Saved';
      toast.success(msg);
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this ledger?')) return;
    try { await api.delete('/ledgers/' + id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const approve = async (id) => {
    if (!window.confirm('Approve this ledger? It will become locked from staff edits.')) return;
    try { await api.post('/ledgers/' + id); toast.success('Approved'); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const reject = async (id) => {
    if (!window.confirm('Reject this ledger? It will be removed permanently.')) return;
    try { await api.post('/ledgers/' + id + '/reject'); toast.success('Rejected'); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const edit = (l) => { setForm({ ...empty, ...l }); setEditId(l.id); setOpen(true); };

  const filtered = list.filter(l => {
    if (filterType !== 'all') {
      if (filterType === 'supplier' && !['supplier','both'].includes(l.party_type)) return false;
      if (filterType === 'customer' && !['customer','both'].includes(l.party_type)) return false;
    }
    if (filterApproval !== 'all' && l.approval_status !== filterApproval) return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return (l.name || '').toLowerCase().includes(needle)
        || (l.phone || '').includes(q)
        || (l.ledger_code || '').toLowerCase().includes(needle)
        || (l.city || '').toLowerCase().includes(needle);
  });


  const pendingCount = list.filter(l => l.approval_status === 'pending').length;
  return (
    <div className="card">
      <div className="card-header">
        <h3>Ledgers ({filtered.length})
          {pendingCount > 0 && (
            <span style={{marginLeft:10,padding:'2px 10px',background:'#fef3c7',color:'#92400e',borderRadius:12,fontSize:12,fontWeight:600}}>
              {pendingCount} pending
            </span>
          )}
        </h3>
        <button className="btn btn-blue" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}>
          <Plus size={14}/> Add Ledger
        </button>
      </div>

      <div className="search-bar" style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
        <Search size={16} style={{color:'#94a3b8'}}/>
        <input
          placeholder="Search name, phone, code, or city…"
          value={q}
          onChange={e=>setQ(e.target.value)}
          style={{flex:1,minWidth:200}}
        />
        <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{maxWidth:180}}>
          <option value="all">All Ledgers</option>
          <option value="supplier">Suppliers Only</option>
          <option value="customer">Customers Only</option>
        </select>
        <select value={filterApproval} onChange={e=>setFilterApproval(e.target.value)} style={{maxWidth:160}}>
          <option value="all">Any Approval</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
        </select>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Type</th>
              <th>Phone</th>
              <th>City</th>
              <th>Vehicle</th>
              <th className="td-num">Opening</th>
              <th>Approval</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length ? filtered.map(l => {
              const badge = approvalBadge(l.approval_status);
              const canMod = canModifyRecord(l);
              const isPending = l.approval_status === 'pending';
              return (
                <tr key={l.id} style={isPending ? {background:'#fffbeb'} : {}}>
                  <td><strong>{l.ledger_code}</strong></td>
                  <td>{l.name}</td>
                  <td><span className={'badge ' + typeBadge(l.party_type)}>{typeLabel(l.party_type)}</span></td>
                  <td>{l.phone || '-'}</td>
                  <td>{l.city || '-'}</td>
                  <td>{l.default_vehicle_no || <span style={{color:'#94a3b8'}}>-</span>}</td>
                  <td className="td-num">
                    {formatCurrency(l.opening_balance)}
                    {l.balance_type && parseFloat(l.opening_balance) > 0 && (
                      <small style={{display:'block',color:'#64748b'}}>{l.balance_type === 'payable' ? 'Cr' : 'Dr'}</small>
                    )}
                  </td>
                  <td>
                    <span style={{background:badge.bg,color:badge.color,padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:600,display:'inline-block'}}>
                      {badge.label}
                    </span>
                    {l.created_by_name && (
                      <small style={{display:'block',color:'#64748b',fontSize:10,marginTop:2}}>by {l.created_by_name}</small>
                    )}
                  </td>
                  <td>
                    {admin && isPending && (
                      <>
                        <button className="btn btn-sm" style={{background:'#10b981',color:'white'}} onClick={()=>approve(l.id)} title="Approve"><Check size={12}/></button>
                        {' '}
                        <button className="btn btn-sm" style={{background:'#ef4444',color:'white'}} onClick={()=>reject(l.id)} title="Reject"><X size={12}/></button>
                        {' '}
                      </>
                    )}
                    {canMod && (
                      <>
                        <button className="btn btn-sm btn-gray" onClick={()=>edit(l)} title="Edit"><Edit2 size={12}/></button>
                        {' '}
                        <button className="btn btn-sm btn-red" onClick={()=>del(l.id)} title="Delete"><Trash2 size={12}/></button>
                      </>
                    )}
                    {!canMod && !isPending && (
                      <small style={{color:'#94a3b8',fontSize:11}}>locked</small>
                    )}
                  </td>
                </tr>
              );
            }) : <tr><td colSpan="9" className="empty">No ledgers found</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title={editId ? 'Edit Ledger' : 'Add Ledger'} size="lg"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>{editId ? 'Update' : (admin ? 'Create' : 'Submit for approval')}</button>
        </>}>
        {!admin && !editId && (
          <div style={{padding:'10px 14px',background:'#fef3c7',color:'#92400e',borderRadius:8,marginBottom:12,fontSize:13}}>
            ⓘ This record will be submitted for admin approval. It will become active once approved.
          </div>
        )}
        <div className="form-grid">
          <div className="form-group">
            <label>Name *</label>
            <input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} placeholder="Party / company name"/>
          </div>
          <div className="form-group">
            <label>Type *</label>
            <select value={form.party_type} onChange={e=>setForm({...form, party_type:e.target.value})}>
              <option value="customer">Customer (We sell to them)</option>
              <option value="supplier">Supplier (We buy from them)</option>
              <option value="both">Both (Buy & Sell)</option>
            </select>
          </div>
          <div className="form-group"><label>Contact Person</label><input value={form.contact_person} onChange={e=>setForm({...form, contact_person:e.target.value})}/></div>
          <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/></div>
          <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/></div>
          <div className="form-group"><label>GSTIN</label><input value={form.gstin} onChange={e=>setForm({...form, gstin:e.target.value})}/></div>
          <div className="form-group"><label>City</label><input value={form.city} onChange={e=>setForm({...form, city:e.target.value})}/></div>
          <div className="form-group"><label>State</label><input value={form.state} onChange={e=>setForm({...form, state:e.target.value})}/></div>
          <div className="form-group"><label>Pincode</label><input value={form.pincode} onChange={e=>setForm({...form, pincode:e.target.value})}/></div>
          <div className="form-group"><label>Credit Limit</label><input type="number" value={form.credit_limit} onChange={e=>setForm({...form, credit_limit:e.target.value})}/></div>
          <div className="form-group"><label>Opening Balance</label><input type="number" value={form.opening_balance} onChange={e=>setForm({...form, opening_balance:e.target.value})}/></div>
          <div className="form-group"><label>Opening Date</label><input type="date" value={form.opening_balance_date||''} onChange={e=>setForm({...form, opening_balance_date:e.target.value})}/></div>
          <div className="form-group">
            <label>Balance Type</label>
            <select value={form.balance_type} onChange={e=>setForm({...form, balance_type:e.target.value})}>
              <option value="receivable">Receivable (They owe us)</option>
              <option value="payable">Payable (We owe them)</option>
            </select>
          </div>
          <div className="form-group">
            <label>Status</label>
            <select value={form.status} onChange={e=>setForm({...form, status:e.target.value})}>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>
          <div className="form-group">
            <label>Vehicle (optional)</label>
            <select value={form.default_vehicle_id || ''} onChange={e=>setForm({...form, default_vehicle_id:e.target.value})}>
              <option value="">-- None --</option>
              {vehicles.map(v => (
                <option key={v.id} value={v.id}>{v.vehicle_no}{v.vehicle_type ? ` · ${v.vehicle_type}` : ''}</option>
              ))}
            </select>
            <small style={{color:'#64748b',fontSize:11}}>The vehicle most often used for this party.</small>
          </div>
          <div className="form-group full-width"><label>Address</label><textarea value={form.address} onChange={e=>setForm({...form, address:e.target.value})}/></div>
          <div className="form-group full-width"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
        </div>
      </Modal>
    </div>
  );
}
